/** @jsx jsx */
import { useEffect, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { Link } from '@helpcenter/analytics-components';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { ScreenName } from '@helpcenter/constants';
import ExternalResourceIcon from '@helpcenter/external-resource-fallback-icon';
import { ThemedLink } from '@helpcenter/themed-link';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';
import messages from 'view/help-center-topics/topic/messages';
import { useIsPageInEditMode } from 'view/layout-next/controllers';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import withAnalyticsEvents from '@atlaskit/analytics-next/withAnalyticsEvents';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import IconTile from '@atlaskit/icon/icon-tile';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { OPERATIONAL_EVENT_TYPE, TRACK_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { AnalyticsScreen } from '@atlassian/help-center-common-util/analytics/analytics-screen';
import { FireScreenEventOnMount } from '@atlassian/help-center-common-util/analytics/fire-screen-event';
import { addSourceToUrl } from '@atlassian/help-center-common-util/article-util';
import {
	isExternalResource,
	urlRefsAndTarget,
} from '@atlassian/help-center-common-util/is-external-resource';
import { getUpdatedDisplayLink } from '@atlassian/help-center-common-util/url';
import type { topicCardFragment$key } from './__generated__/topicCardFragment.graphql';
import type { iconTypes } from '@helpcenter/external-resource-fallback-icon/src/types';

const HC_ADS_DROP_COMMIT_2 = 'hc_ads_drop_commit_2';

const topicNameLinkStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	fontWeight: token('font.weight.medium', '500'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '100%',
	'@media (min-width: 480px)': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '16px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '24px',
		fontWeight: token('font.weight.semibold', '600'),
	},
});

const topicNameWithoutLinkStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '14px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '20px',
	fontWeight: token('font.weight.medium', '500'),
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '100%',
	color: token('color.text', '#172B4D'),
	'@media (min-width: 480px)': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '16px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '24px',
		fontWeight: token('font.weight.semibold', '600'),
	},
});

const helpObjectsStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '12px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	listStyleType: 'none',
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	marginTop: token('space.200', '16px'),
	// Upgrading @atlaskit/platform; fix these violations.
	// eslint-disable-next-line @atlaskit/platform/expand-spacing-shorthand
	padding: token('space.0', '0rem'),
	'@media (min-width: 480px)': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '14px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
	},
});

const listItemStyles = css({
	marginTop: 0,
});

const containerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	height: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& ol': {
		// Upgrading @atlaskit/platform; fix these violations.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles,@atlaskit/platform/expand-spacing-shorthand
		margin: `${token('space.200', '16px')} 0 0 !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& a': {
		marginTop: 0,
	},
});

const styledHelpObjectStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: token('space.300', '24px'),
	display: 'flex',
	marginBottom: token('space.150', '0.75rem'),
});

const helpObjectSpacingStyles = css({
	gap: token('space.100', '8px'),
	alignItems: 'center',
});

const imageContainerStyles = css({
	height: '24px',
	width: '24px',
	marginRight: token('space.100', '8px'),
	flexShrink: 0,
});

const helpObjectTitleStyles = css({
	whiteSpace: 'nowrap',
	width: '100%',
	color: token('color.text', '#172B4D'),
	textOverflow: 'ellipsis',
	overflow: 'hidden',
});

const showMoreLinkStyles = css({
	marginTop: 'auto',
});

const topicLinkStyles = css({
	overflow: 'hidden',
});

const textContentStyles = css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const showMoreWithoutLinkStyles = css({
	color: token('color.text', '#172B4D'),
});

export interface Props {
	topic: topicCardFragment$key;
}

export const Topic = (props: Props & WithAnalyticsEventsProps) => {
	di(useIsPageInEditMode);
	const { topicName, items, topicId } = useFragment<topicCardFragment$key>(
		graphql`
			fragment topicCardFragment on HelpLayoutTopic {
				topicName
				topicId
				items {
					__id
					title
					logo
					displayLink
					entityKey
					helpObjectType
				}
			}
		`,
		props.topic,
	);

	const intl = useIntl();
	const [isPageInEditMode] = useIsPageInEditMode();
	const MAX_HELP_OBJECTS = 3;

	const allHelpObjects = useMemo(() => items || [], [items]);
	const { createAnalyticsEvent } = props;

	useEffect(() => {
		!isPageInEditMode &&
			allHelpObjects.forEach((o) => {
				if (!o?.title) {
					if (createAnalyticsEvent) {
						const analyticsEvent = createAnalyticsEvent({
							analyticsType: OPERATIONAL_EVENT_TYPE,
							action: 'invalidHelpObjectFound',
							actionSubject: 'helpObject',
						});
						analyticsEvent.fire();
					}
				}
			});
	}, [isPageInEditMode, allHelpObjects, createAnalyticsEvent]);

	const showMoreCount = allHelpObjects.length - MAX_HELP_OBJECTS;

	const totalArticleCount = useMemo(() => {
		return allHelpObjects.filter((o) => o?.helpObjectType === 'HelpObjectStoreArticle').length;
	}, [allHelpObjects]);

	useEffect(() => {
		createAnalyticsEvent?.({
			topicId,
			articlesCount: totalArticleCount,
			analyticsType: TRACK_EVENT_TYPE,
			action: 'viewed',
			actionSubject: 'helpCenterTopic',
		}).fire();
	}, [totalArticleCount, createAnalyticsEvent, topicId]);

	const getResourceType = (helpObjectType: string): iconTypes => {
		switch (helpObjectType) {
			case 'HelpObjectStoreArticle':
				return 'ARTICLE';
			case 'HelpObjectStoreRequestForm':
				return 'REQUEST_FORM';
			case 'HelpObjectStoreChannel':
				return 'CHANNEL';
			default:
				return 'REQUEST_FORM';
		}
	};

	const isHcAdsDropCommit2Enabled = FeatureGates.checkGate(HC_ADS_DROP_COMMIT_2);

	if (allHelpObjects.length) {
		return (
			<AnalyticsContext data={{ attributes: { topicId, articlesCount: totalArticleCount } }}>
				<div css={containerStyles}>
					{isPageInEditMode ? (
						<div css={topicNameWithoutLinkStyles}>
							<div css={textContentStyles}>{topicName}</div>
						</div>
					) : (
						<ThemedLink
							css={topicNameLinkStyles}
							to={topicId ? `/topic/${topicId}` : ''}
							actionSubjectId={'topicTitle'}
						>
							<Tooltip content={topicName}>
								<div css={textContentStyles}>{topicName}</div>
							</Tooltip>
						</ThemedLink>
					)}
					<ul css={helpObjectsStyles}>
						{allHelpObjects.slice(0, MAX_HELP_OBJECTS).map((helpObject, index) => (
							<li key={helpObject?.__id} css={listItemStyles}>
								<AnalyticsContext
									data={{
										attributes: {
											topicId,
											displayLink: helpObject.displayLink,
											entity: helpObject.entityKey,
											helpObjectPosition: index + 1,
										},
									}}
								>
									{helpObject && (
										<div
											css={[
												styledHelpObjectStyles,
												isHcAdsDropCommit2Enabled && helpObjectSpacingStyles,
											]}
											data-testid="help-object-container"
										>
											{helpObject.logo &&
												(isHcAdsDropCommit2Enabled ? (
													<IconTile
														size="medium"
														label=""
														appearance="gray"
														icon={() => (
															<AutoScaledImage
																src={helpObject.logo ?? undefined}
																alt={helpObject.title ?? ''}
																inheritWidth
															/>
														)}
													/>
												) : (
													<div css={imageContainerStyles}>
														<AutoScaledImage
															src={helpObject.logo}
															alt={helpObject.title ?? ''}
															inheritWidth
														/>
													</div>
												))}
											{!helpObject.logo &&
												helpObject?.helpObjectType &&
												(isHcAdsDropCommit2Enabled ? (
													<IconTile
														size="medium"
														label=""
														appearance="gray"
														icon={() => (
															<ExternalResourceIcon
																iconUrl={getUpdatedDisplayLink(helpObject?.displayLink || '')}
																resourceType={getResourceType(helpObject?.helpObjectType || '')}
																size={'medium'}
															/>
														)}
													/>
												) : (
													<div css={imageContainerStyles}>
														<ExternalResourceIcon
															iconUrl={getUpdatedDisplayLink(helpObject?.displayLink || '')}
															resourceType={getResourceType(helpObject?.helpObjectType)}
															size={'medium'}
														/>
													</div>
												))}
											{isPageInEditMode ? (
												<div css={helpObjectTitleStyles}>{helpObject.title}</div>
											) : (
												<AnalyticsScreen screenName={ScreenName.TOPIC_ITEM_HOMEPAGE}>
													<Link
														css={topicLinkStyles}
														{...urlRefsAndTarget({
															isAnExternalResource: isExternalResource(
																helpObject?.entityKey as string,
															),
															displayLink:
																getResourceType(helpObject?.helpObjectType || '') === 'ARTICLE'
																	? addSourceToUrl(
																			getUpdatedDisplayLink(helpObject?.displayLink || ''),
																			'topic',
																			isExternalResource(helpObject?.entityKey as string),
																		)
																	: getUpdatedDisplayLink(helpObject?.displayLink || ''),
														})}
														attributes={{
															topicId,
															displayLink: helpObject.displayLink,
															entity: helpObject.entityKey,
														}}
														actionSubjectId="helpObject"
													>
														<Tooltip content={helpObject.title}>
															<div css={helpObjectTitleStyles}>{helpObject.title}</div>
														</Tooltip>
														<FireScreenEventOnMount />
													</Link>
												</AnalyticsScreen>
											)}
										</div>
									)}
								</AnalyticsContext>
							</li>
						))}
					</ul>
					{showMoreCount > 0 &&
						(isPageInEditMode ? (
							<div css={showMoreWithoutLinkStyles}>
								<div css={textContentStyles}>
									{intl.formatMessage(messages.showMoreText, {
										count: showMoreCount,
									})}
								</div>
							</div>
						) : (
							<ThemedLink
								css={showMoreLinkStyles}
								to={topicId ? `/topic/${topicId}` : ''}
								actionSubjectId={'showMoreHelpObjects'}
							>
								<div css={textContentStyles}>
									{intl.formatMessage(messages.showMoreText, {
										count: showMoreCount,
									})}
								</div>
							</ThemedLink>
						))}
				</div>
			</AnalyticsContext>
		);
	}
	return null;
};

export default withAnalyticsEvents()(Topic);
